import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { aiAPI } from '../../lib/api';
import { 
  Robot, 
  PaperPlaneTilt, 
  User, 
  Sparkle, 
  Trash, 
  ArrowClockwise,
  FirstAidKit,
  Stethoscope,
  ClipboardText,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const suggestedPrompts = {
  patient: [
    { icon: FirstAidKit, text: "Help me book an appointment" },
    { icon: ClipboardText, text: "What documents do I need for my visit?" },
    { icon: Stethoscope, text: "What should I know before my appointment?" },
    { icon: Warning, text: "When should I seek emergency care?" },
  ],
  cma: [
    { icon: Stethoscope, text: "Help me triage this patient's symptoms" },
    { icon: ClipboardText, text: "What care gaps should I look for?" },
    { icon: Warning, text: "Identify high-risk indicators" },
  ],
  provider: [
    { icon: Stethoscope, text: "Suggest differential diagnoses" },
    { icon: ClipboardText, text: "Help document this encounter" },
    { icon: Warning, text: "Review potential drug interactions" },
  ],
  admin: [
    { icon: ClipboardText, text: "Analyze population health trends" },
    { icon: Warning, text: "Identify operational bottlenecks" },
    { icon: Sparkle, text: "Suggest quality improvement initiatives" },
  ],
};

export default function AIAssistantPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Generate a unique session ID for this conversation
    setSessionId(`session-${user?.id}-${Date.now()}`);
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await aiAPI.chat({
        message: messageText.trim(),
        session_id: sessionId,
        context: `User role: ${user?.role}. User name: ${user?.first_name} ${user?.last_name}.`
      });
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('AI Chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(`session-${user?.id}-${Date.now()}`);
    toast.success('Chat cleared');
  };

  const prompts = suggestedPrompts[user?.role] || suggestedPrompts.patient;

  const getRoleGreeting = () => {
    switch (user?.role) {
      case 'patient':
        return "I'm here to help you understand your health information, prepare for appointments, and answer general wellness questions.";
      case 'cma':
        return "I can assist with patient triage, identify care gaps, and help with clinical documentation.";
      case 'provider':
        return "I can help with clinical decision support, documentation, and identifying potential care issues.";
      case 'admin':
        return "I can help analyze operational data, identify trends, and suggest improvements.";
      default:
        return "How can I assist you today?";
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Robot size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                AI Assistant
              </h1>
              <p className="text-xs text-slate-500">Powered by GPT-5.2</p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              data-testid="clear-chat-btn"
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash size={18} />
              Clear chat
            </button>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {messages.length === 0 ? (
          <div className="max-w-2xl mx-auto">
            {/* Welcome Message */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkle size={32} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Hello, {user?.first_name}!
              </h2>
              <p className="text-slate-500 max-w-md mx-auto">
                {getRoleGreeting()}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Remember: Always consult your healthcare provider for medical decisions.
              </p>
            </div>

            {/* Suggested Prompts */}
            <div className="grid gap-3">
              <p className="text-sm font-medium text-slate-700 mb-1">Suggested questions:</p>
              {prompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(prompt.text)}
                  data-testid={`suggested-prompt-${index}`}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left group"
                >
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                    <prompt.icon size={20} className="text-teal-600" />
                  </div>
                  <span className="text-slate-700 group-hover:text-slate-900">{prompt.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                data-testid={`message-${message.id}`}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Robot size={18} className="text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-teal-600 text-white'
                      : message.isError
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  <p className={`text-xs mt-2 ${
                    message.role === 'user' ? 'text-teal-200' : 'text-slate-400'
                  }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-slate-600" />
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Robot size={18} className="text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <ArrowClockwise size={18} className="text-teal-500 animate-spin" />
                    <span className="text-slate-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="px-8 py-4 border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything..."
                data-testid="chat-input"
                rows={1}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              data-testid="send-message-btn"
              className="px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <PaperPlaneTilt size={20} weight="fill" />
            </button>
          </div>
          <p className="text-xs text-center text-slate-400 mt-2">
            AI responses are for informational purposes only. Always verify with a healthcare professional.
          </p>
        </div>
      </div>
    </div>
  );
}
